import React, { useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, User, Trophy, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useKiprData } from '@/lib/kiprData';

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, authEnabled } = useAuth();
  const { profile, isAdmin } = useKiprData();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-inter">
      {/* Top Navigation */}
      <header className="bg-white border-b border-[#DADADA] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="text-2xl font-bold font-poppins text-[#1A1A1A] hover:text-[#555555] transition-colors"
          >
            Kipr
          </button>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-2 text-[#555555]">
              <Trophy size={16} className="text-yellow-500" />
              <span className="text-sm font-medium">{profile.level}</span>
            </div>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  location.pathname.startsWith('/admin')
                    ? 'text-[#121212] bg-[#F5F5F5]'
                    : 'text-[#555555] hover:text-[#1A1A1A] hover:bg-[#F5F5F5]'
                }`}
              >
                <ShieldCheck size={16} />
                Admin
              </button>
            )}
            <button
              onClick={() => navigate('/profile')}
              className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                location.pathname === '/profile'
                  ? 'text-[#121212] bg-[#F5F5F5]'
                  : 'text-[#555555] hover:text-[#1A1A1A] hover:bg-[#F5F5F5]'
              }`}
              type="button"
            >
              {profile.avatar ? (
                <img
                  src={profile.avatar}
                  alt={profile.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <User size={20} />
              )}
            </button>
            {authEnabled && (
              <button
                onClick={() => void signOut()}
                className="flex items-center px-3 py-2 rounded-md text-[#555555] hover:text-[#1A1A1A] hover:bg-[#F5F5F5] transition-colors"
                aria-label="Sign out"
              >
                <LogOut size={18} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto px-6 py-8 pb-8">
        <Outlet />
      </main>
      <footer className="border-t border-[#E8E8E8] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-[#777777]">
          <span>© {new Date().getFullYear()} Kipr</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-[#1A1A1A] transition-colors">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-[#1A1A1A] transition-colors">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;